import { useEffect, useMemo, useState } from 'react';
import { goFishRank, goFishSort, type GoFishAsk, type GoFishLegal, type GoFishView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const RANK_NAMES: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
const rankName = (r: number) => RANK_NAMES[r] ?? String(r);

export function GoFishTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as GoFishView;
  const legal = useGame((s) => s.legal) as GoFishLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [rank, setRank] = useState<number | null>(null);
  const hand = useMemo(() => goFishSort(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && !!turn?.seats.includes(mySeat) && view.phase === 'ASK';
  const narrow = useNarrow();
  const size = narrow ? 'md' : 'lg';
  const ended = view.phase === 'MATCH_END';
  const ranks = legal?.ranks ?? [];
  const targets = legal?.targets ?? [];

  // A new turn (or a new hand after a hit) starts from a clean selection.
  useEffect(() => setRank(null), [view.turn, view.hand.length, view.phase]);

  const pick = (c: string) => {
    if (!myTurn) return;
    const r = goFishRank(c);
    if (ranks.includes(r)) setRank(rank === r ? null : r);
  };
  const askFor = (target: number) => {
    if (rank !== null) void act({ type: 'ask', target, rank });
  };

  const line = (a: GoFishAsk) => {
    const p = { a: name(a.seat), b: name(a.target), rank: rankName(a.rank), n: a.got };
    if (a.got > 0) return t('gf.logHit', p);
    if (a.luck) return t('gf.logLuck', p);
    return a.fished ? t('gf.logMiss', p) : t('gf.logNoLuck', p);
  };
  const recent = view.log.slice(-3);
  const leader = Math.max(...view.totals);
  const hint = !myTurn ? null : rank === null ? t('gf.pickHint') : t('gf.askHint', { rank: rankName(rank) });
  const ranking = [0, 1, 2, 3].sort((a, b) => (view.totals[b] ?? 0) - (view.totals[a] ?? 0) || a - b);

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{t('gf.cards', { n: view.handCounts[s] ?? 0 })} · {t('gf.books', { n: view.totals[s] ?? 0 })}</span>}
            tags={
              (view.books[s]?.length ?? 0) > 0 ? (
                <span className="tag seen" title={t('gf.booksOf')}>
                  {view.books[s]!.map(rankName).join(' ')}
                </span>
              ) : null
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">{t('gf.stock', { n: view.stock })}</div>
          <div aria-label="Recent asks" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', minHeight: 70 }}>
            {recent.length === 0 && <span className="dim">{t('gf.empty')}</span>}
            {recent.map((a, i) => (
              <span key={view.log.length - recent.length + i} className="pill" style={{ opacity: i === recent.length - 1 ? 1 : 0.6, fontSize: 'var(--fs-xs)' }}>
                {line(a)}
              </span>
            ))}
          </div>
          {ended && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{t('gf.wins', { name: name(ranking[0] ?? 0), n: leader })}</strong>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('gf.cards', { n: view.handCounts[mySeat] ?? 0 })} · {t('gf.books', { n: view.totals[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>{hint}</span>
            {targets.map((s) => (
              <button key={s} className="btn primary" disabled={acting || rank === null} onClick={() => askFor(s)}>
                {t('gf.ask', { name: name(s) })}
              </button>
            ))}
          </div>
        )}
        <Hand size={size} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={size}
              dealIndex={view.log.length === 0 ? i : undefined}
              selected={rank !== null && goFishRank(c) === rank}
              playable={myTurn && ranks.includes(goFishRank(c))}
              unplayable={myTurn && !ranks.includes(goFishRank(c))}
              onClick={() => pick(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('gf.player')}</th>
              <th>{t('gf.booksCol')}</th>
              <th>{t('gf.cardsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                <td className="num">{view.totals[s] ?? 0}</td>
                <td className="num">{view.handCounts[s] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('gf.scoreHint')}</p>
      </Modal>
    </>
  );
}
